// Output calibration. A click track is scheduled on the AudioContext clock; the
// user taps each click; the median lag of those taps is the output's offset.
// Both sides use the same heard-time axis: a click at context time c is heard at c.

export const CLICKS = 40;
export const CLICK_PERIOD_S = 0.5;
/** The first click lands this many seconds after the press. */
export const CLICK_LEAD_S = 1;
/** Taps farther than this from every click are ignored. */
export const WINDOW_S = 0.25;
const MIN_TAPS = 10;

/**
 * Schedule the clicks from context time t0: a 1 kHz burst of about 15 ms each.
 * Everything routes through the returned bus; disconnecting it silences the run.
 */
export function scheduleClicks(ctx: AudioContext, t0: number): GainNode {
  const bus = ctx.createGain();
  bus.connect(ctx.destination);
  for (let i = 0; i < CLICKS; i++) {
    const c = t0 + i * CLICK_PERIOD_S;
    const osc = ctx.createOscillator();
    const env = ctx.createGain();
    osc.frequency.value = 1000;
    env.gain.setValueAtTime(0, c);
    env.gain.linearRampToValueAtTime(1, c + 0.001);
    env.gain.linearRampToValueAtTime(0, c + 0.015);
    osc.connect(env).connect(bus);
    osc.start(c);
    osc.stop(c + 0.015);
  }
  return bus;
}

/**
 * Offset from taps made on the clicks: the median of (tap minus nearest click)
 * over taps within WINDOW_S of a click. Null when fewer than MIN_TAPS are valid.
 */
export function fitCalibration(taps: readonly number[], t0: number): { offsetS: number; valid: number } | null {
  const errors: number[] = [];
  for (const tap of taps) {
    const i = Math.min(CLICKS - 1, Math.max(0, Math.round((tap - t0) / CLICK_PERIOD_S)));
    const err = tap - (t0 + i * CLICK_PERIOD_S);
    if (Math.abs(err) <= WINDOW_S) errors.push(err);
  }
  if (errors.length < MIN_TAPS) return null;
  errors.sort((a, b) => a - b);
  const m = errors.length >> 1;
  const offsetS = errors.length % 2 ? errors[m]! : (errors[m - 1]! + errors[m]!) / 2;
  return { offsetS, valid: errors.length };
}

/** Calibration key for the output: the AudioContext's sinkId when the browser names one. */
export function deviceKey(ctx: AudioContext): string {
  const sink = (ctx as AudioContext & { sinkId?: unknown }).sinkId;
  return typeof sink === 'string' && sink !== '' ? sink : 'default';
}
